import React, {useCallback, useContext, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {ContextualMenu, OptionInterface, TableData} from '@veneer/core';

import {License, LicenseOrdering} from '@br/idm-api';
import {useCustomerApi} from '@br/idm-components';

import {GlobalTranslationsContext, useIntlFormatter, useProductTypeNames} from '../../i18n';
import {UseColumnsHook, UseMapDataHook} from '../DataTable';
import {
    DateRangeFilter,
    ProductFilter,
    ProductSkuFilter,
    ProductTypeFilter,
    TextFilter,
    TrueFalseFilter,
} from '../filters';

import {LicenseColumns, useLicenseTransferContext} from './TransferLicenseModal';
import type {LicenseFilters} from './table';

const useLicenseColumns: UseColumnsHook<LicenseFilters, LicenseColumns, LicenseOrdering> = (filters, setFilters) => {
    const {t} = useTranslation(['licenses']);
    const {productApi} = useCustomerApi();
    const getProductSkus = useCallback(() => productApi.productList(), [productApi]);

    return useMemo(
        () => [
            {
                id: 'product',
                label: t('licenses:product', 'Product'),
                sortable: false,
                filter: (
                    <ProductFilter
                        label={t('licenses:product', 'Product')}
                        field="product"
                        filters={filters}
                        setFilters={setFilters}
                        getProductSkus={getProductSkus}
                    />
                ),
            },
            {
                id: 'productSku',
                label: t('licenses:productDescription', 'Product Description'),
                sortable: false,
                filter: (
                    <ProductSkuFilter
                        label={t('licenses:productDescription', 'Product Description')}
                        field="productSku"
                        filters={filters}
                        setFilters={setFilters}
                        getProductSkus={getProductSkus}
                    />
                ),
            },
            {
                id: 'productType',
                label: t('licenses:productType', 'Product Type'),
                sortable: false,
                filter: (
                    <ProductTypeFilter
                        label={t('licenses:productType', 'Product Type')}
                        field="productType"
                        filters={filters}
                        setFilters={setFilters}
                    />
                ),
            },
            {
                id: 'licenseNumber',
                label: t('licenses:licenseNumber', 'License Number'),
                sortable: false,
                filter: (
                    <TextFilter
                        label={t('licenses:licenseNumber', 'License Number')}
                        field="licenseNumber"
                        filters={filters}
                        setFilters={setFilters}
                    />
                ),
            },
            {
                id: 'deviceCount',
                label: t('licenses:purchased', 'Purchased'),
                sortable: true,
            },
            {
                id: 'seatsInUse',
                label: t('licenses:used', 'Used'),
                sortable: true,
            },
            {
                id: 'activationDate',
                label: t('licenses:activationDate', 'Activation Date'),
                sortable: true,
                filter: (
                    <DateRangeFilter
                        field="activationDate"
                        filters={filters}
                        setFilters={setFilters}
                    />
                ),
            },
            {
                id: 'expiryDate',
                label: t('licenses:expiryDate', 'Expiry Date'),
                sortable: true,
                filter: (
                    <DateRangeFilter
                        field="expiryDate"
                        filters={filters}
                        setFilters={setFilters}
                    />
                ),
            },
            {
                id: 'isSubscription',
                label: t('licenses:subscription', 'Subscription'),
                sortable: false,
                filter: (
                    <TrueFalseFilter
                        data-testid="subscription-filter"
                        label={t('licenses:subscription', 'Subscription')}
                        field="isSubscription"
                        filters={filters}
                        setFilters={setFilters}
                    />
                ),
            },
            {
                id: 'autoRenew',
                label: t('licenses:autoRenew', 'Auto Renew'),
                sortable: false,
                filter: (
                    <TrueFalseFilter
                        data-testid="autorenew-filter"
                        label={t('licenses:autoRenew', 'Auto Renew')}
                        field="autoRenew"
                        filters={filters}
                        setFilters={setFilters}
                    />
                ),
            },
        ],
        [t, filters, setFilters, getProductSkus]
    );
};

const TenantLicenseCommand = {
    transfer: 0,
} as const;
type TenantLicenseCommand = (typeof TenantLicenseCommand)[keyof typeof TenantLicenseCommand];

type ActionHandler = (license: License, action: TenantLicenseCommand) => void;
const useMapLicenseActions = (handler: ActionHandler): ((license: License) => TableData) => {
    const {t} = useTranslation(['licenses']);

    const handleMenuClick = useCallback((license: License, handler: ActionHandler) => {
        return (e: React.SyntheticEvent, action: OptionInterface<TenantLicenseCommand>) => {
            handler(license, action.value);
        };
    }, []);

    return useCallback(
        license => {
            const actionOptions: OptionInterface<TenantLicenseCommand>[] = [];
            if (license.isTransferable) {
                actionOptions.push({
                    value: TenantLicenseCommand.transfer,
                    label: t('licenses:transfer', 'Transfer'),
                });
            }
            const action =
                actionOptions.length > 0 ? (
                    <ContextualMenu
                        options={actionOptions}
                        onClick={handleMenuClick(license, handler)}
                    />
                ) : undefined;

            return {
                license,
                rowConfig: {
                    action,
                },
            };
        },
        [handleMenuClick, handler, t]
    );
};

const useMapLicenseData: UseMapDataHook<License, LicenseColumns> = () => {
    const {setTargetLicense} = useLicenseTransferContext();

    const handler = useCallback(
        (licence: License, action: TenantLicenseCommand) => {
            switch (action) {
                case TenantLicenseCommand.transfer:
                    setTargetLicense(licence);
            }
        },
        [setTargetLicense]
    );

    const {t} = useTranslation(['licenses']);
    const {
        general: {yesNo},
    } = useContext(GlobalTranslationsContext);

    const {formatDate, formatNumber} = useIntlFormatter();
    const productTypeNames = useProductTypeNames();
    const mapLicenseActions = useMapLicenseActions(handler);

    return useCallback(
        license => ({
            license: license,
            product: license.product.edition.product.name,
            productSku: license.product.name,
            productType: productTypeNames[license.product.skuType],
            licenseNumber: license.licenseNumber,
            deviceCount: formatNumber(license.deviceCount),
            seatsInUse: formatNumber(license.seatsInUse),
            expiryDate: license.expiryDate ? formatDate(license.expiryDate) : t('licenses:notApplicable', 'N/A'),
            activationDate: formatDate(license.activationDate),
            isSubscription: yesNo(license.subscription !== null),
            autoRenew: yesNo(license.subscription?.autoRenewal),
            ...mapLicenseActions(license),
        }),
        [productTypeNames, formatNumber, t, formatDate, yesNo, mapLicenseActions]
    );
};

export {useLicenseColumns, useMapLicenseData, useMapLicenseActions, ActionHandler, TenantLicenseCommand};
